/**
 * Method group extracted from the Konva map Stimulus controller.
 * These functions intentionally use `this` so the thin Stimulus adapter can
 * keep the existing target/value/action API while the implementation lives in
 * smaller feature-oriented modules.
 */
import {
  DRAW_WIDTH,
  ERASE_RADIUS,
  MAX_FOG_BRUSH_SIZE,
  MIN_FOG_BRUSH_SIZE,
  MAX_SCALE,
  MIN_SCALE,
  MIN_SHAPE_DIMENSION,
  POSITION_ZOOM_STEP,
  SHAPE_COLORS,
  SHAPE_DEFAULTS,
  SHAPE_TRANSFORMER_OPTIONS,
  TOOLBAR_DEFAULT_TOOLS,
  TOOLBAR_TOOLS,
  TOOLBAR_URL_PARAM,
  ZOOM_STEP
} from './constants';

export const LifecycleMethods = {
  connect() {
    this._mapConnected = true;
    if (this.playerValue) this._playerRefreshTimer = setInterval(() => this._refreshPlayerMap(), 2000);
    this._handleVisibility = () => {
      if (document.hidden) {
        this._cancelCanvasGesture();
        this._finishShapeAnimations?.();
        this._fogPresentation?.finish();
        this._background?.pause();
      } else if (this._stage) {
        this._background?.start();
      } else if (!this._background && !this._backgroundError) {
        this._queueMapReload();
      }
    };
    this._handleModalClosing = (event) => {
      if (!event.defaultPrevented && event.target.contains(this.element)) this._disposeMapStage();
    };
    document.addEventListener('visibilitychange', this._handleVisibility);
    // Frame visits can cache the page while this editor remains connected.
    // Release the live canvas on disconnect, not when Turbo takes a snapshot.
    document.addEventListener('modal:closing', this._handleModalClosing);
    this._connectMobileEditor();
    this._connectToolbarMemory();
    this._restoreToolbarVisibility();
    this._init();
    this._handleKeyDown = this._onKeyDown.bind(this);
    this._handleTurboMorph = this._onTurboMorph.bind(this);
    document.addEventListener('keydown', this._handleKeyDown, true);
    document.addEventListener('turbo:morph', this._handleTurboMorph);
    this._handleComponentMorph = (event) => {
      if (event.target === this.element) this._onTurboMorph();
    };
    this.element.addEventListener('turbo:morph-element', this._handleComponentMorph);
    this._handleFrameRender = (event) => {
      if (event.target.contains(this.element)) this._onTurboMorph();
      // Modal navigation can morph the existing target instead of reconnecting it.
      if (this.hasShapeEditModalTarget && event.target.contains(this.shapeEditModalTarget)) this._refreshEditModal();
    };
    document.addEventListener('turbo:frame-render', this._handleFrameRender);
  },

  disconnect() {
    clearInterval(this._playerRefreshTimer);
    this._cancelCanvasGesture();
    this._rememberEditorState();
    this._disconnectMobileEditor();
    this._unwatchShapeModal();
    this._disconnectToolbarMemory();
    if (this._autosaveEnabled !== false && this._pendingCanvasJson) this._flushCanvasSave?.();
    if (this._autosaveEnabled !== false && this._pendingPositionJson) this._flushPositionSave?.();
    if (this._wheelViewport && this._handleWheel) {
      this._wheelViewport.removeEventListener('wheel', this._handleWheel);
      this._wheelViewport = null;
    }
    clearTimeout(this._positionSaveDebounce);
    clearTimeout(this._canvasSaveDebounce);
    if (this._sortable) {
      this._sortable.destroy();
      this._sortable = null;
    }
    this._sortables?.forEach((sortable) => sortable.destroy());
    this._mapConnected = false;
    this._disposeMapStage();
    document.removeEventListener('visibilitychange', this._handleVisibility);
    document.removeEventListener('modal:closing', this._handleModalClosing);
    if (this._handleKeyDown) document.removeEventListener('keydown', this._handleKeyDown, true);
    if (this._handleTurboMorph) document.removeEventListener('turbo:morph', this._handleTurboMorph);
    this.element.removeEventListener('turbo:morph-element', this._handleComponentMorph);
    if (this._handleFrameRender) document.removeEventListener('turbo:frame-render', this._handleFrameRender);
  },

  _disposeMapStage() {
    this._endGridCalibration?.({ restoreTool: false });
    this._finishShapeAnimations?.();
    this._movementDistances?.clear();
    this._cancelCanvasGesture();
    this._clearCharacterMove?.();
    this._lastCharacterMove = null;
    this._rememberEditorState();
    this._disconnectCanvasTouch();
    this._touchGesture = null;
    this._hideShapePopover?.();
    if (this.hasFogBrushPopoverTarget) this._setToolbarPopover(this.fogBrushPopoverTarget);
    this._clearAreaEffectPreview();
    this._clearCharacterPreview();
    this._pendingCharacterToken = null;
    this._clearEffectRotationHandle();
    this._pendingAreaEffect = null;
    this._mapLoad = (this._mapLoad || 0) + 1;
    this._rememberReadonlyPositionTransform?.();
    this._positionResizeObserver?.disconnect();
    this._positionTransformTween?.destroy();
    this._positionTransformTween = null;
    cancelAnimationFrame(this._readonlyPositionAnimationFrame);
    this._readonlyPositionAnimationFrame = null;
    this._background?.dispose();
    this._background = null;
    this._gridOverlay?.destroy();
    this._gridOverlay = null;
    this._fogPresentation?.destroy();
    this._fogPresentation = null;
    this._fogReady = false;
    this._stage?.destroy();
    this._stage = null;
    delete this.element.dataset.konvaMapReady;
  },

  _queueMapReload() {
    if (!this._mapConnected || this._mapReloadQueued) return;
    this._mapReloadQueued = true;
    queueMicrotask(() => {
      this._mapReloadQueued = false;
      if (this._mapConnected) this._reinitializeStage();
    });
  },

  gridEnabledValueChanged() {
    this._syncGridControls?.();
    for (const link of this.characterToolTargets || []) link.hidden = !this.gridEnabledValue;
    for (const notice of this.characterGridNoticeTargets || []) notice.hidden = this.gridEnabledValue;
    if (!this.gridEnabledValue && this._pendingCharacterToken) this._applyTool('drag');
    if (!this.gridEnabledValue && this._characterMove) this.cancelCharacterMove();
    this._gridOverlay?.render();
    this._syncMobileControls?.();
  },
  gridHiddenValueChanged() {
    this._syncGridControls?.();
    this._gridOverlay?.render();
  },
  gridDetectionApproximateValueChanged() {
    this._syncGridControls?.();
  },
  gridOffsetXValueChanged() {
    this._gridOverlay?.render();
    this._syncAreaEffectGeometry();
  },
  gridOffsetYValueChanged() {
    this._gridOverlay?.render();
    this._syncAreaEffectGeometry();
  },
  gridSizeValueChanged() {
    this._gridOverlay?.render();
    this._syncAreaEffectGeometry();
  },
  gridWidthValueChanged() {
    this._gridOverlay?.render();
  },
  gridColorValueChanged() {
    this._gridOverlay?.render();
  },

  hiddenValueChanged(value, previousValue) {
    // The first player poll materializes the default false attribute. Stimulus
    // reports that attribute change even though visibility has not changed;
    // rebuilding here would discard an in-progress movement preview.
    if (value !== previousValue) this._queueMapReload();
  },
  imageContentTypeValueChanged() {
    this._queueMapReload();
  },

  reinitializeValueChanged() {
    this._queueMapReload();
  },

  imageUrlValueChanged() {
    this._queueMapReload();
  },

  _reinitializeStage() {
    this._disposeMapStage();
    this._fogLayer = null;
    this._shapesLayerBelow = null;
    this._shapesLayerAbove = null;
    this._shapes = [];
    this._shapeCounter = 0;
    this._selectedShapeId = null;
    this._editingShapeId = null;
    this._init();
  },

  canvasJsonValueChanged() {
    // An upload response can arrive before the autosave has finished.
    const pending = this._pendingCanvasJson || this._savingCanvasJson || this._failedCanvasJson;
    if (!this.readonlyValue && pending && this.canvasJsonValue !== pending) {
      this.canvasJsonValue = pending;
      return;
    }
    if (!this._stage || !this._fogLayer) return;
    // Updating the snapshot after a local edit must not recreate selected shapes.
    if (this.canvasJsonValue === JSON.stringify(this._toJson())) return;
    try {
      this._restoreFromJson(JSON.parse(this.canvasJsonValue), { animate: true });
      // Rails may normalize a just-saved document during a settings refresh.
      // Only a different server revision invalidates this editor's history.
      this._initializeHistory?.({ reset: this._historyRevision !== this.canvasRevisionValue });
    } catch (e) {
      console.warn('Could not restore canvas JSON:', e);
    }
  },

  positionZoomValueChanged() {
    this._syncPositionManagerControls();
    if (this._positionManagerActive) this._applyPositionManagerTransform();
    else if (this.readonlyValue) this._scheduleReadonlyPositionTransform();
  },

  positionPosXValueChanged() {
    this._syncPositionManagerControls();
    if (this._positionManagerActive) this._applyPositionManagerTransform();
    else if (this.readonlyValue) this._scheduleReadonlyPositionTransform();
  },

  positionPosYValueChanged() {
    this._syncPositionManagerControls();
    if (this._positionManagerActive) this._applyPositionManagerTransform();
    else if (this.readonlyValue) this._scheduleReadonlyPositionTransform();
  },

  positionWindowWidthValueChanged() {
    this._applyPositionWindowDimensions();
    if (this._positionManagerActive) this._applyPositionManagerTransform();
    else if (this.readonlyValue) this._scheduleReadonlyPositionTransform();
  },

  positionWindowHeightValueChanged() {
    this._applyPositionWindowDimensions();
    if (this._positionManagerActive) this._applyPositionManagerTransform();
    else if (this.readonlyValue) this._scheduleReadonlyPositionTransform();
  },

  positionCharactersWidthValueChanged() {
    this._applyPositionWindowDimensions();
  },

  positionCharactersHeightValueChanged() {
    this._applyPositionWindowDimensions();
  },

  characterPlacementValueChanged() {
    this._applyPositionWindowDimensions();
  },

  effectiveCharacterPlacementValueChanged() {
    this._applyPositionWindowDimensions();
  },

  gameViewRotationValueChanged() {
    this._applyPositionWindowDimensions();
    if (this._positionManagerActive) this._applyPositionManagerTransform();
    else if (this.readonlyValue) this._scheduleReadonlyPositionTransform();
  },

  drawBrushSizeValueChanged() {
    const size = this._clampFogBrushSize(this.drawBrushSizeValue);
    if (size !== this.drawBrushSizeValue) {
      this.drawBrushSizeValue = size;
      return;
    }
    this._updateFogBrushCursorSize();
    this._syncFogBrushPopover();
  },

  eraseBrushSizeValueChanged() {
    const size = this._clampFogBrushSize(this.eraseBrushSizeValue);
    if (size !== this.eraseBrushSizeValue) {
      this.eraseBrushSizeValue = size;
      return;
    }
    this._updateFogBrushCursorSize();
    this._syncFogBrushPopover();
  }
};
